import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, App, Button, Checkbox, Collapse, Descriptions, Empty, Input, InputNumber, Modal, Segmented, Space, Spin, Table, Tag,
  Typography, theme,
} from 'antd';
import {
  CalendarOutlined, CheckCircleFilled, CheckOutlined, CloseCircleOutlined, EnvironmentOutlined, HistoryOutlined, RollbackOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { inputNumberMoneyProps } from '../../utils/currency';
import {
  ASAMA_ADI, DURUM_RENK, FisKucuk, GecmisListesi, ISARET_TANIM, IsaretBandi, IsaretEtiketleri, KONUM_TANIM, KonumEtiketi, OGUN_ADI,
  REZ_ASAMA_ADI, SeyahatAkisi, TIP_ADI, ciddiMi, gunAdi, gunTutari, hata, kisaTarih, para, tarih, useHarcirahApi,
} from './ortak';
import { GIDER_GIREN, GiderBolumu } from './giderler';

/**
 * Onay Kutusu (24.10.2026) — routes/harcirah.py /harcirah-onay. Amir → finans → son onay; geri gönderilen kayıt
 * çalışana döner ve yeniden gönderilince baştan başlar. Şehir/tarih/aynı fiş işaretleri kırmızı ve büyük gösterilir;
 * işaretli seyahatte "işaretleri gördüm" + not zorunlu, toplu onay yalnız işaretsizlere. Test sürecinde kişi
 * kilidindeki kullanıcı her aşamada TEST VEKİLİ olarak karar verebilir (geçmişe "yerine" yazılır).
 * 26.10.2026: iki akış aynı kutuda — seyahat / rezervasyon (amir plan onayı, finans fiyat onayı + limit, finans harcama
 * kontrolü, yönetici; geri gönderilen bir önceki adıma döner) ve yemek harcırahı (amir → finans → son onay).
 * 30.10.2026 ekran ayrımı: kapsam = finans (Finans ekranı: finans aşamaları) | amir (Onaylarım: amir + yönetim
 * aşamaları) | tumu (toplu görünüm); backend /harcirah-onay?kapsam= ile süzer.
 * 30.10.2026: seyahat onayında şehir + tarih büyük kutularda, amir aşamasında öğün hakları gün gün var / yok rozetiyle.
 */
const { Text } = Typography;
// Yemek akışında yalnız çalışanın girdiği gider (yakıt) işaretleri, seyahat akışında rezervasyon kalemleri
const rolIsaretleri = (s, rol) => Object.fromEntries(Object.entries(s.gider_isaretleri || {})
  .filter(([gid]) => GIDER_GIREN[(s.giderler || []).find((g) => g.id === gid)?.tur] === rol));
const REZ_ONCEKI = { amir: 'plan', finans_onay: 'fiyat', finans_kontrol: 'islem', yonetici: 'finans_kontrol' };
const REZ_ONAY_DUGMESI = {
  amir: 'Planı Onayla', finans_onay: 'Fiyatı Onayla, Kartı Aç', finans_kontrol: 'Harcamaları Onayla', yonetici: 'Son Onayı Ver',
};

const GUN_TAM = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
const gunTam = (v) => GUN_TAM[(dayjs(v).day() + 6) % 7];

const isaretSayisi = (sayilar) => Object.values(sayilar || {}).reduce((a, n) => a + n, 0);
const ciddiSayisi = (sayilar) => Object.entries(sayilar || {}).filter(([t]) => ISARET_TANIM[t]?.ciddi).reduce((a, [, n]) => a + n, 0);

function SayiEtiketleri({ sayilar }) {
  const turler = Object.keys(ISARET_TANIM).filter((t) => sayilar?.[t]);
  if (!turler.length) return <Text type="secondary">—</Text>;
  return (
    <Space size={[4, 4]} wrap>
      {turler.map((t) => (
        <Tag key={t} color={ISARET_TANIM[t].renk} icon={ISARET_TANIM[t].ikon} style={{ fontWeight: ISARET_TANIM[t].ciddi ? 700 : 400, marginInlineEnd: 0 }}>
          {sayilar[t]} {ISARET_TANIM[t].ad}
        </Tag>
      ))}
    </Space>
  );
}

function OgunHucresi({ s, g, ogun }) {
  if (!g[ogun]) return <Text type="danger">Hak Yok</Text>;
  const t = g[`${ogun}_tutar`];
  if (t == null) return <Text type="secondary">Girilmedi</Text>;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <FisKucuk seyahatId={s.id} gunTarihi={g.tarih} ogun={ogun} fis={g[`${ogun}_fis`]} />
      <div style={{ minWidth: 0 }}>
        <Text strong style={{ fontSize: 15 }}>{para(t)}</Text>
        <IsaretEtiketleri isaretler={s.isaretler?.[`${g.tarih}|${ogun}`]} />
      </div>
    </div>
  );
}

function OnayPenceresi({ id, onKapat, onKarar }) {
  const api = useHarcirahApi('/harcirah-onay');
  const { message } = App.useApp();
  const [s, setS] = useState(null);
  const [notu, setNotu] = useState('');
  const [goruldu, setGoruldu] = useState(false);
  const [bekliyor, setBekliyor] = useState(false);

  useEffect(() => {
    if (!id) return;
    setS(null);
    setNotu('');
    setGoruldu(false);
    api.get(`/${id}`).then(setS).catch((e) => { message.error(hata(e)); onKapat(); });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const o = s?.onay;
  const calisanIsaretleri = s ? rolIsaretleri(s, 'calisan') : {};
  const isaretli = s && (Object.keys(s.isaretler || {}).length > 0 || Object.keys(calisanIsaretleri).length > 0);
  const onaylanabilir = !isaretli || (goruldu && notu.trim().length >= 3);

  const karar = (onayla) => {
    if (!onayla && notu.trim().length < 3) {
      message.warning('Geri gönderme nedenini not alanına yazın.');
      return;
    }
    setBekliyor(true);
    api.post(`/${id}/${onayla ? 'onayla' : 'geri'}`, { asama: s.durum, notu: notu.trim(), isaretler_goruldu: goruldu })
      .then(() => { message.success(onayla ? 'Onaylandı.' : 'Geri gönderildi.'); onKarar(); })
      .catch((e) => message.error(hata(e)))
      .finally(() => setBekliyor(false));
  };

  const gunCiddi = (g) => ['ogle', 'aksam'].some((x) => ciddiMi(s.isaretler?.[`${g.tarih}|${x}`]));

  return (
    <Modal rootClassName="hr-modal" open={!!id} onCancel={onKapat} width={980} destroyOnHidden
      title={s ? `${s.personel_adi} · ${s.il_ad} · ${tarih(s.baslangic)}–${tarih(s.bitis)}` : 'Seyahat'}
      footer={o?.karar_verebilir ? (
        <div style={{ textAlign: 'left' }}>
          {o.vekil && (
            <Alert type="info" showIcon style={{ marginBottom: 8 }}
              message={<span><b>TEST VEKİLİ:</b> {ASAMA_ADI[s.durum]} kararını <b>{o.yerine}</b> yerine veriyorsunuz; geçmişe böyle yazılır.</span>} />
          )}
          {isaretli && (
            <Checkbox checked={goruldu} onChange={(e) => setGoruldu(e.target.checked)} style={{ marginBottom: 8 }}>
              <b>İşaretleri ve çalışanın açıklamalarını gördüm</b>
            </Checkbox>
          )}
          <Input.TextArea rows={2} maxLength={500} value={notu} onChange={(e) => setNotu(e.target.value)}
            placeholder={isaretli ? 'Not (işaretli seyahatte onay için zorunlu; geri göndermede neden)' : 'Not (geri göndermede zorunlu)'} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <Button onClick={onKapat}>Kapat</Button>
            <Button danger icon={<RollbackOutlined />} loading={bekliyor} onClick={() => karar(false)}>Geri Gönder</Button>
            <Button type="primary" icon={<CheckOutlined />} loading={bekliyor} disabled={!onaylanabilir} onClick={() => karar(true)}>
              {ASAMA_ADI[s.durum]} Ver
            </Button>
          </div>
        </div>
      ) : [<Button key="k" onClick={onKapat}>Kapat</Button>]}
    >
      {!s ? <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div> : (
        <>
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3, lg: 3, xl: 3, xxl: 3 }} bordered style={{ marginBottom: 12 }}
            items={[
              { label: 'Durum', children: <Tag color={DURUM_RENK[s.durum]}>{s.durum_adi}</Tag> },
              { label: 'Seyahat Türü', children: s.seyahat_turu_adi },
              { label: 'Tip', children: `${s.tip} — ${TIP_ADI[s.tip]}` },
              { label: 'Amir', children: s.amir_adi },
              { label: 'Yemek Harcırahı', children: <Text strong style={{ fontSize: 16 }}>{para(s.toplam)}</Text> },
              { label: 'Ulaşım', children: s.ulasim_adi || '—' },
              { label: 'Şirket Giderleri', children: para(s.gider_toplam) },
              { label: 'Seyahat Maliyeti', children: <Text strong>{para(s.maliyet)}</Text> },
              ...(s.konum?.beklenen ? [{ label: 'Konum', children: `${s.konum.dogrulanan}/${s.konum.beklenen} Gün Doğrulandı` }] : []),
              { label: 'Açıklama', span: 'filled', children: s.aciklama || '—' },
            ]} />
          <IsaretBandi isaretler={s.isaretler} giderIsaretleri={calisanIsaretleri} />
          {/* 03.11.2026 günlük konum: doğrulanmayan gün gönderimi engellemez; çalışan açıklar, karar onaycının */}
          {s.konum?.eksik?.length > 0 && (
            <Alert type="error" showIcon style={{ marginBottom: 12 }}
              message={<b>{s.konum.eksik.length} günün konumu doğrulanmadı: {s.konum.eksik.map(kisaTarih).join(', ')}</b>}
              description="Çalışan o gün seyahat ilinin sınırları içinden konum doğrulaması yapmadı. Açıklaması Konum sütununda; değerlendirerek karar verin." />
          )}
          <Table size="small" bordered pagination={false} rowKey="tarih" dataSource={s.gunler} scroll={{ x: 900 }}
            rowClassName={(g) => (gunCiddi(g) || s.konum?.gunler?.[g.tarih]?.durum === 'eksik' ? 'ok-isaretli-satir' : '')}
            columns={[
              { title: 'Gün', dataIndex: 'tarih', width: 110, render: (v) => <span><b>{gunAdi(v)}</b> {tarih(v)}</span> },
              { title: 'Konum', width: 190, render: (_, g) => {
                const o = s.konum?.gunler?.[g.tarih];
                if (!KONUM_TANIM[o?.durum]) return <Text type="secondary">—</Text>;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                    <KonumEtiketi o={o} />
                    {o.aciklama && <Text style={{ fontSize: 12 }}>Çalışanın Açıklaması: “{o.aciklama}”</Text>}
                  </div>
                );
              } },
              { title: 'Öğle', render: (_, g) => <OgunHucresi s={s} g={g} ogun="ogle" /> },
              { title: 'Akşam', render: (_, g) => <OgunHucresi s={s} g={g} ogun="aksam" /> },
              { title: 'Gün Toplamı', align: 'right', width: 110, render: (_, g) => (gunTutari(g) ? para(gunTutari(g)) : '') },
            ]}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}><b>Toplam</b></Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  {s.konum?.beklenen ? <Text type="secondary">{s.konum.dogrulanan} / {s.konum.beklenen} gün doğrulandı</Text> : null}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} colSpan={2}><Text type="secondary">{s.girilen_ogun} / {s.acik_ogun} öğün girildi; girilmeyenler ödenmez</Text></Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right"><b>{para(s.toplam)}</b></Table.Summary.Cell>
              </Table.Summary.Row>
            )} />
          <div style={{ marginTop: 16, fontWeight: 600 }}>Ulaşım Ve Konaklama <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>· şirket öder, çalışana geri ödenmez</Text></div>
          <div style={{ marginTop: 8 }}><GiderBolumu s={s} baslik={false} /></div>
          <Collapse ghost style={{ marginTop: 8 }}
            items={[{ key: 'g', label: <span><HistoryOutlined /> Geçmiş ({s.gecmis.length})</span>, children: <GecmisListesi gecmis={s.gecmis} /> }]} />
        </>
      )}
    </Modal>
  );
}

// --- Seyahat / rezervasyon akışı kararı ------------------------------------------------------------------------
const OZET_STILI = `
  .ro-ozet { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 10px; margin-bottom: 12px; }
  .ro-kutu { border: 1px solid var(--ro-kenar); border-radius: 10px; padding: 10px 14px; background: var(--ro-dolgu); min-width: 0; }
  .ro-etiket { font-size: 12px; color: var(--ro-ikincil); text-transform: uppercase; letter-spacing: .04em; }
  .ro-buyuk { font-size: 22px; font-weight: 700; line-height: 1.3; margin-top: 2px; overflow-wrap: anywhere; }
  .ro-ok { color: var(--ro-ikincil); font-weight: 400; margin: 0 4px; }
  .ro-alt { color: var(--ro-ikincil); margin-top: 2px; }
  .ro-ogun { border: 1px solid var(--ro-kenar); border-radius: 10px; padding: 10px 14px; margin-bottom: 12px; }
  .ro-ogun-bas { display: flex; justify-content: space-between; align-items: baseline; flex-wrap: wrap; gap: 4px 12px; margin-bottom: 10px; }
  .ro-gunler { display: grid; grid-template-columns: repeat(auto-fill, minmax(124px, 1fr)); gap: 8px; }
  .ro-gun { border: 1px solid var(--ro-kenar); border-radius: 8px; padding: 6px 8px; display: flex; flex-direction: column; gap: 4px; }
  .ro-rozet { display: flex; align-items: center; gap: 6px; border-radius: 6px; padding: 2px 8px; font-size: 13px; }
  .ro-rozet.var { background: var(--ro-basari-zemin); border: 1px solid var(--ro-basari-kenar); color: var(--ro-basari); font-weight: 600; }
  .ro-rozet.yok { background: var(--ro-hata-zemin); border: 1px solid var(--ro-hata-kenar); color: var(--ro-hata); }
`;

function useOzetDegiskenleri() {
  const { token } = theme.useToken();
  return {
    '--ro-kenar': token.colorBorderSecondary, '--ro-kenar2': token.colorBorder, '--ro-dolgu': token.colorFillAlter,
    '--ro-ikincil': token.colorTextSecondary, '--ro-silik': token.colorTextTertiary, '--ro-basari': token.colorSuccessText,
    '--ro-basari-zemin': token.colorSuccessBg, '--ro-basari-kenar': token.colorSuccessBorder,
    '--ro-hata': token.colorError, '--ro-hata-zemin': token.colorErrorBg, '--ro-hata-kenar': token.colorErrorBorder,
  };
}

// Nereye + ne zaman: onaycının ilk bakışta gördüğü iki kutu
function SeyahatOzeti({ s }) {
  const gun = dayjs(s.bitis).diff(dayjs(s.baslangic), 'day') + 1;
  return (
    <div className="ro-ozet">
      <div className="ro-kutu">
        <div className="ro-etiket"><EnvironmentOutlined /> Gidilecek Şehir</div>
        <div className="ro-buyuk">{s.il_ad}</div>
        <div className="ro-alt">{s.aciklama || s.seyahat_turu_adi}</div>
      </div>
      <div className="ro-kutu">
        <div className="ro-etiket"><CalendarOutlined /> Tarih</div>
        {gun === 1 ? (
          <>
            <div className="ro-buyuk">{tarih(s.baslangic)}</div>
            <div className="ro-alt">{gunTam(s.baslangic)} · Günübirlik</div>
          </>
        ) : (
          <>
            <div className="ro-buyuk">{tarih(s.baslangic)}<span className="ro-ok">→</span>{tarih(s.bitis)}</div>
            <div className="ro-alt">{gunTam(s.baslangic)} – {gunTam(s.bitis)} · <b>{gun} Gün, {gun - 1} Gece</b></div>
          </>
        )}
      </div>
    </div>
  );
}

// Öğün hakları gün gün: var (yeşil, ✓) / yok (kırmızı, ✗; kapalı harcırah her ekranda kırmızı) — yazı da var
function OgunPlani({ s }) {
  const gunler = s.gunler || [];
  const say = (o) => gunler.filter((g) => g[o]).length;
  const toplam = say('ogle') + say('aksam');
  return (
    <div className="ro-ogun">
      <div className="ro-ogun-bas">
        <span className="ro-etiket">Yemek Harcırahı · Öğün Hakları</span>
        <span>
          Tip {s.tip} ({TIP_ADI[s.tip]}) ·{' '}
          <b>{toplam ? `${say('ogle')} Öğle + ${say('aksam')} Akşam = ${toplam} Öğün` : 'Öğün Hakkı Yok'}</b>
        </span>
      </div>
      <div className="ro-gunler">
        {gunler.map((g) => (
          <div key={g.tarih} className="ro-gun">
            <div><b>{gunAdi(g.tarih)}</b> {kisaTarih(g.tarih)}</div>
            {['ogle', 'aksam'].map((o) => (g[o] ? (
              <span key={o} className="ro-rozet var"><CheckCircleFilled /> {OGUN_ADI[o]} Var</span>
            ) : (
              <span key={o} className="ro-rozet yok"><CloseCircleOutlined /> {OGUN_ADI[o]} Yok</span>
            )))}
          </div>
        ))}
      </div>
    </div>
  );
}

function RezOnayPenceresi({ id, onKapat, onKarar }) {
  const api = useHarcirahApi('/harcirah-onay');
  const { message } = App.useApp();
  const [s, setS] = useState(null);
  const [notu, setNotu] = useState('');
  const [goruldu, setGoruldu] = useState(false);
  const [limit, setLimit] = useState(null);
  const [bekliyor, setBekliyor] = useState(false);
  const degiskenler = useOzetDegiskenleri();

  useEffect(() => {
    if (!id) return;
    setS(null);
    setNotu('');
    setGoruldu(false);
    api.get(`/${id}`).then((d) => { setS(d); setLimit(d.rez_toplam || null); })
      .catch((e) => { message.error(hata(e)); onKapat(); });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const o = s?.rez_onay;
  const asama = s?.rez_durum;
  const isaretli = ['finans_kontrol', 'yonetici'].includes(asama) && Object.keys(o?.isaret_sayilari || {}).length > 0;
  const onaylanabilir = (!isaretli || (goruldu && notu.trim().length >= 3)) && (asama !== 'finans_onay' || limit > 0);

  const karar = (onayla) => {
    if (!onayla && notu.trim().length < 3) {
      message.warning('Geri gönderme nedenini not alanına yazın.');
      return;
    }
    setBekliyor(true);
    api.post(`/${id}/rez-karar`, {
      asama, onayla, notu: notu.trim(), isaretler_goruldu: goruldu, ...(asama === 'finans_onay' && onayla ? { limit } : {}),
    }).then(() => { message.success(onayla ? 'Onaylandı.' : 'Geri gönderildi.'); onKarar(); })
      .catch((e) => message.error(hata(e)))
      .finally(() => setBekliyor(false));
  };

  return (
    <Modal rootClassName="hr-modal" open={!!id} onCancel={onKapat} width={980} destroyOnHidden
      title={s ? `${s.personel_adi} — ${REZ_ASAMA_ADI[asama] || s.rez_durum_adi}` : 'Seyahat'}
      footer={o?.karar_verebilir ? (
        <div style={{ textAlign: 'left' }}>
          {o.vekil && (
            <Alert type="info" showIcon style={{ marginBottom: 8 }}
              message={<span><b>TEST VEKİLİ:</b> {REZ_ASAMA_ADI[asama]} kararını <b>{o.yerine}</b> yerine veriyorsunuz; geçmişe böyle yazılır.</span>} />
          )}
          {asama === 'finans_onay' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <Text strong>Onaylanan Tutar (Kart Limiti):</Text>
              <InputNumber {...inputNumberMoneyProps} min={0} precision={2} suffix="₺" value={limit} onChange={setLimit} style={{ width: 200 }} />
              <Text type="secondary" style={{ fontSize: 12 }}>Girilen fiyatlar {para(s.rez_toplam)}; ödeme bu tutarı aşarsa kontrol aşamasında işaretlenir.</Text>
            </div>
          )}
          {isaretli && (
            <Checkbox checked={goruldu} onChange={(e) => setGoruldu(e.target.checked)} style={{ marginBottom: 8 }}>
              <b>İşaretleri ve açıklamaları gördüm</b>
            </Checkbox>
          )}
          <Input.TextArea rows={2} maxLength={500} value={notu} onChange={(e) => setNotu(e.target.value)}
            placeholder={isaretli ? 'Not (işaretli seyahatte onay için zorunlu; geri göndermede neden)' : 'Not (geri göndermede zorunlu)'} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <Button onClick={onKapat}>Kapat</Button>
            <Button danger icon={<RollbackOutlined />} loading={bekliyor} onClick={() => karar(false)}>
              Geri Gönder → {REZ_ASAMA_ADI[REZ_ONCEKI[asama]]}
            </Button>
            <Button type="primary" icon={<CheckOutlined />} loading={bekliyor} disabled={!onaylanabilir} onClick={() => karar(true)}>
              {REZ_ONAY_DUGMESI[asama]}
            </Button>
          </div>
        </div>
      ) : [<Button key="k" onClick={onKapat}>Kapat</Button>]}
    >
      {!s ? <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div> : (
        <div style={degiskenler}>
          <style>{OZET_STILI}</style>
          <SeyahatOzeti s={s} />
          <div style={{ marginBottom: 12 }}><SeyahatAkisi s={s} /></div>
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3, lg: 3, xl: 3, xxl: 3 }} bordered style={{ marginBottom: 12 }}
            items={[
              { label: 'Seyahat Türü', children: s.seyahat_turu_adi },
              ...(asama !== 'amir' ? [{ label: 'Tip', children: `${s.tip} — ${TIP_ADI[s.tip]}` }] : []),
              { label: 'Amir', children: s.amir_adi },
              { label: 'Ulaşım', children: s.ulasim_adi || '—' },
              { label: 'Otel', children: s.konaklama_gerekli ? 'Gerekli' : 'Gerekmiyor' },
              ...(s.rez_notu ? [{ label: 'Rezervasyon Talebi', span: 'filled', children: <Text strong>{s.rez_notu}</Text> }] : []),
              ...(asama !== 'amir' ? [{ label: 'Rezervasyon', children: <Text strong>{para(s.rez_toplam)}</Text> }] : []),
              ...(s.rez_limit != null ? [{ label: 'Finans Limiti', children: <Text type={s.limit_asimi ? 'danger' : undefined} strong={s.limit_asimi}>{para(s.rez_limit)}{s.limit_asimi ? ' · AŞILDI' : ''}</Text> }] : []),
            ]} />
          {asama === 'amir' ? (
            <>
              <Alert type="info" showIcon style={{ marginBottom: 12 }}
                message={s.rez_gerekli
                  ? 'Onaylarsanız rezervasyon personeli otel / bilet fiyatlarını girer; ardından finans onaylar.'
                  : 'Otel ve uçak gerekmiyor: onaylarsanız seyahat planı onaylanmış olur.'} />
              <OgunPlani s={s} />
            </>
          ) : (
            <>
              {s.limit_asimi && (
                <Alert type="error" showIcon style={{ marginBottom: 12 }}
                  message={<b>{ISARET_TANIM.limit.ad}: Rezervasyon {para(s.rez_toplam)}, Finans Limiti {para(s.rez_limit)}</b>} />
              )}
              <IsaretBandi isaretler={{}} giderIsaretleri={rolIsaretleri(s, 'rezervasyon')} />
              <GiderBolumu s={s} baslik={false} />
            </>
          )}
          <Collapse ghost style={{ marginTop: 8 }}
            items={[{ key: 'g', label: <span><HistoryOutlined /> Geçmiş ({s.gecmis.length})</span>, children: <GecmisListesi gecmis={s.gecmis} /> }]} />
        </div>
      )}
    </Modal>
  );
}

const KAPSAM_ACIKLAMA = {
  finans: 'Finans onayları: seyahat fiyat onayı (kart limiti), harcama kontrolü ve yemek harcırahının finans onayı.',
  amir: 'Amiri olduğunuz seyahatlerin plan ve yemek harcırahı onayları; yönetimdeyseniz son onaylar da burada.',
};

// Liste Konum sütunu (03.11.2026 kullanıcı "konum sütunu da olsun"): yemek harcırahında doğrulanan / beklenen gün.
// Özellik öncesi (muaf) seyahatte beklenen 0 → "—".
function KonumOzeti({ k }) {
  if (!k?.beklenen) return <Text type="secondary">—</Text>;
  const eksik = k.eksik.length;
  const durum = eksik ? 'eksik' : k.dogrulanan < k.beklenen ? 'bugun' : 'dogrulandi';
  const t = KONUM_TANIM[durum];
  return (
    <span>
      <Tag color={t.renk} icon={t.ikon} style={{ marginInlineEnd: 0 }}>
        {eksik ? `${eksik} Gün Doğrulanmadı` : durum === 'bugun' ? t.ad : `${k.dogrulanan}/${k.beklenen} Gün Doğrulandı`}
      </Tag>
      {durum !== 'dogrulandi' && (
        <div style={{ marginTop: 4 }}><Text type="secondary" style={{ fontSize: 11 }}>{k.dogrulanan}/{k.beklenen} gün doğrulandı</Text></div>
      )}
    </span>
  );
}

export default function OnayKutusu({ onSayi, kapsam = 'tumu' }) {
  const api = useHarcirahApi('/harcirah-onay');
  const { message, modal } = App.useApp();
  const [veri, setVeri] = useState(null);
  const [gorunum, setGorunum] = useState('bekleyen');
  const [secili, setSecili] = useState([]);
  const [acik, setAcik] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);

  const yukle = useCallback(() => {
    setYukleniyor(true);
    api.get('', kapsam === 'tumu' ? {} : { kapsam }).then((d) => { setVeri(d); setSecili([]); onSayi?.(d.bekleyen.length); })
      .catch((e) => message.error(hata(e))).finally(() => setYukleniyor(false));
  }, [api, message, onSayi, kapsam]);
  useEffect(() => { yukle(); }, [yukle]);

  const liste = gorunum === 'bekleyen' ? veri?.bekleyen || [] : veri?.kararlarim || [];
  const isaretli = (r) => isaretSayisi(r.isaret_sayilari) > 0;
  const rez = (r) => r.akis === 'rezervasyon';

  const topluOnay = () => {
    const secilen = liste.filter((r) => secili.includes(r.anahtar) && !rez(r));
    const kalemler = secilen.map((r) => ({ id: r.id, asama: r.durum }));
    modal.confirm({
      title: `${kalemler.length} yemek harcırahı onaylansın mı?`,
      content: `Toplam ${para(secilen.reduce((a, r) => a + r.toplam, 0))}. İşaretsiz harcırahlar; her biri kendi aşamasında onaylanır.`,
      okText: 'Onayla', cancelText: 'Vazgeç',
      onOk: () => api.post('/toplu', { kalemler }).then((sonuc) => {
        const basarisiz = sonuc.filter((x) => !x.tamam);
        if (basarisiz.length) message.warning(`${sonuc.length - basarisiz.length} onaylandı, ${basarisiz.length} onaylanamadı: ${basarisiz[0].hata}`);
        else message.success(`${sonuc.length} seyahat onaylandı.`);
        yukle();
      }).catch((e) => message.error(hata(e))),
    });
  };

  const kolonlar = [
    { title: 'Personel', dataIndex: 'personel_adi', render: (v, r) => <span><b>{v}</b>{gorunum === 'bekleyen' && r.vekil && <div><Text type="secondary" style={{ fontSize: 11 }}>Vekil: {r.yerine} Yerine</Text></div>}</span> },
    { title: 'Yer · Tarih', render: (_, r) => <span>{r.il_ad} · {tarih(r.baslangic)} – {tarih(r.bitis)}<div><Text type="secondary" style={{ fontSize: 11 }}>{r.seyahat_turu_adi} · Tip {r.tip} · Amir {r.amir_adi}</Text></div></span> },
    {
      title: gorunum === 'bekleyen' ? 'Onay' : 'Şu Anki Durum', dataIndex: 'durum',
      render: (v, r) => (
        <span>
          <Tag color={rez(r) ? 'purple' : 'blue'} style={{ marginInlineEnd: 0 }}>{rez(r) ? 'Seyahat / Rezervasyon' : 'Yemek Harcırahı'}</Tag>
          <div style={{ marginTop: 4 }}><Text style={{ fontSize: 12 }}>{gorunum === 'bekleyen' ? r.asama_adi : r.durum_adi}</Text></div>
        </span>
      ),
    },
    {
      title: 'Tutar', align: 'right',
      render: (_, r) => (rez(r) ? (
        <span><b>{para(r.rez_toplam)}</b><div><Text type={r.limit_asimi ? 'danger' : 'secondary'} style={{ fontSize: 11 }}>
          {r.rez_limit != null ? `Limit ${para(r.rez_limit)}` : `Rezervasyon · ${r.ulasim_adi || ''}`}</Text></div></span>
      ) : (
        <span><b>{para(r.toplam)}</b><div><Text type="secondary" style={{ fontSize: 11 }}>Yemek · {r.girilen_ogun}/{r.acik_ogun} Öğün</Text></div></span>
      )),
    },
    { title: 'Konum', width: 175, render: (_, r) => (rez(r) ? <Text type="secondary">—</Text> : <KonumOzeti k={r.konum} />) },
    { title: 'İşaretler', dataIndex: 'isaret_sayilari', render: (v) => <SayiEtiketleri sayilar={v} /> },
    gorunum === 'bekleyen'
      ? { title: 'Gönderim', dataIndex: 'gonderim', render: (v) => (v ? dayjs(v).format('DD.MM HH:mm') : '') }
      : { title: 'Son Karar', dataIndex: 'son_karar', render: (v) => (v ? <span>{['onay', 'rez_onay'].includes(v.islem) ? 'Onay' : 'Geri'} · {ASAMA_ADI[v.asama] || REZ_ASAMA_ADI[v.asama]}<div><Text type="secondary" style={{ fontSize: 11 }}>{v.kisi_adi} · {dayjs(v.zaman).format('DD.MM HH:mm')}</Text></div></span> : '') },
    { title: '', width: 90, render: (_, r) => <Button size="small" type={isaretli(r) ? 'primary' : 'default'} danger={ciddiSayisi(r.isaret_sayilari) > 0} onClick={() => setAcik({ id: r.id, akis: r.akis })}>İncele</Button> },
  ];

  if (!veri) return <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>;

  return (
    <div>
      <style>{`
        .ok-isaretli-satir > td { background: rgba(255, 77, 79, 0.10) !important; }
        .ok-isaretli-satir > td:first-child { box-shadow: inset 4px 0 0 #ff4d4f; }
      `}</style>
      {KAPSAM_ACIKLAMA[kapsam] && <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>{KAPSAM_ACIKLAMA[kapsam]}</Text>}
      {veri.test_vekili && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Test vekili: iki akışın tüm aşamalarında (amir, finans, yönetici) karar verebilirsiniz; kararlar geçmişe “TEST VEKİLİ, … yerine” diye yazılır. Kişi kilidi kalkınca bu özellik kapanır." />
      )}
      <Space wrap style={{ marginBottom: 12, justifyContent: 'space-between', width: '100%' }}>
        <Segmented value={gorunum} onChange={(v) => { setGorunum(v); setSecili([]); }}
          options={[{ value: 'bekleyen', label: `Bekleyen (${veri.bekleyen.length})` }, { value: 'kararlarim', label: 'Karar Verdiklerim' }]} />
        {gorunum === 'bekleyen' && (
          <Space>
            <Text type="secondary" style={{ fontSize: 12 }}>Toplu onay yalnız işaretsiz yemek harcırahları için; seyahat kararlarını açıp verin.</Text>
            <Button type="primary" icon={<CheckOutlined />} disabled={!secili.length} onClick={topluOnay}>
              Seçilenleri Onayla ({secili.length})
            </Button>
          </Space>
        )}
      </Space>
      <Table size="small" bordered rowKey="anahtar" loading={yukleniyor} dataSource={liste} pagination={false} scroll={{ x: 1195 }}
        columns={kolonlar}
        rowClassName={(r) => (ciddiSayisi(r.isaret_sayilari) > 0 ? 'ok-isaretli-satir' : '')}
        rowSelection={gorunum === 'bekleyen' ? {
          selectedRowKeys: secili, onChange: setSecili,
          getCheckboxProps: (r) => ({ disabled: rez(r) || isaretli(r), title: rez(r) ? 'Seyahat kararı tek tek verilir' : isaretli(r) ? 'İşaretli — tek tek inceleyin' : '' }),
        } : undefined}
        locale={{ emptyText: <Empty description={gorunum === 'bekleyen' ? 'Onayınızı bekleyen seyahat yok' : 'Henüz karar vermediniz'} /> }} />
      <OnayPenceresi id={acik?.akis === 'harcirah' ? acik.id : null} onKapat={() => setAcik(null)} onKarar={() => { setAcik(null); yukle(); }} />
      <RezOnayPenceresi id={acik?.akis === 'rezervasyon' ? acik.id : null} onKapat={() => setAcik(null)} onKarar={() => { setAcik(null); yukle(); }} />
    </div>
  );
}
